import React, { useState, useEffect } from 'react';
import Axios from "axios";
import FormFieldRenderer from '../formFieldRenderer/FormFieldRenderer';
import { isFieldVisible, groupFieldsBySteps } from '../../utils/formConditional';

import { PlusCircle, ClipboardList, CheckCircle2, Eye } from 'lucide-react';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

function getLocalizedValue(rawField, isFrench) {
  if (!rawField) return '';
  if (typeof rawField === 'string') return rawField;
  if (typeof rawField === 'object') return isFrench ? (rawField.fr || rawField.en || '') : (rawField.en || '');
  return '';
}

const statusCfg = {
  'not started': { label: 'Not started', cls: 'bg-slate-100 text-slate-500' },
  submitted: { label: 'Submitted', cls: 'bg-blue-50 text-blue-700' },
  reviewed: { label: 'Reviewed', cls: 'bg-emerald-50 text-emerald-700' },
};

const TableForm = (props) => {
  const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;

  const [consultantData, setConsultantData] = useState(null);
  const [openAssign, setOpenAssign] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [viewedForm, setViewedForm] = useState(null);

  useEffect(() => {
    Axios({
      method: "GET",
      withCredentials: true,
      url: `${backEndUrl}/auth/consultantLog`,
    }).then((res) => {
      setConsultantData(res.data);
    });
  }, [backEndUrl]);

  const isFrench = consultantData?.preferredLanguage === 'french';

  const handleAssign = () => {
    if (!selectedTemplateId) return;
    Axios.post(`${backEndUrl}/updateClient/assignFormToClient`, {
      client_id: props.clientId,
      templateId: selectedTemplateId,
    }, { withCredentials: true })
      .then(() => window.location.reload())
      .catch((err) => console.error("Error assigning form:", err));
  };

  const handleMarkReviewed = (form) => {
    Axios.put(`${backEndUrl}/updateClient/reviewForm`, {
      client_id: props.clientId,
      formId: form._id,
    }, { withCredentials: true })
      .then(() => window.location.reload())
      .catch((err) => console.error("Error reviewing form:", err));
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-slate-100 w-full overflow-hidden mt-6 font-sans">
      <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h3 className="font-bold text-sm text-slate-700 uppercase tracking-wider">Online Forms</h3>
        <button
          onClick={() => setOpenAssign(true)}
          className="text-blue-600 text-sm font-bold hover:underline flex items-center gap-1"
        >
          <PlusCircle className="h-4 w-4" /> Assign a Form
        </button>
      </div>

      <div className="overflow-x-auto min-h-[120px]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-slate-500 text-xs font-semibold uppercase tracking-wider border-b border-slate-200/60 bg-slate-50/80">
              <th className="px-8 py-5 pl-8 w-1/2">Form</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5">Submitted</th>
              <th className="px-6 py-5 text-right pr-8">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(props.forms || []).map((form) => {
              const cfg = statusCfg[form.status] || statusCfg['not started'];
              return (
                <tr key={form._id} className="hover:bg-blue-50/50 transition-colors">
                  <td className="px-8 py-5 pl-8 align-top">
                    <div className="flex items-start gap-4">
                      <div className="mt-1 p-2 bg-slate-100 rounded-lg text-slate-500">
                        <ClipboardList className="h-5 w-5" />
                      </div>
                      <span className="font-bold text-slate-800">{getLocalizedValue(form.name, isFrench)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 align-top pt-6">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cfg.cls}`}>
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-6 py-5 align-top pt-6">
                    <span className="text-sm font-medium text-slate-600">
                      {form.dateSubmitted ? new Date(form.dateSubmitted).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-6 py-5 pr-8 align-top pt-6 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {form.status !== 'not started' && (
                        <button
                          onClick={() => setViewedForm(form)}
                          className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-blue-300 hover:text-blue-600 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm transition-all"
                        >
                          <Eye className="h-3 w-3" /> View Answers
                        </button>
                      )}
                      {form.status === 'submitted' && (
                        <button
                          onClick={() => handleMarkReviewed(form)}
                          className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Mark Reviewed
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {(!props.forms || props.forms.length === 0) && (
              <tr><td colSpan="4" className="px-8 py-12 text-center text-slate-500">No forms assigned to this client.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ASSIGN FORM DIALOG */}
      <Dialog open={openAssign} onClose={() => setOpenAssign(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold text-slate-900">Assign a Form</DialogTitle>
        <DialogContent>
          <DialogContentText className="mb-4 text-slate-600 text-sm">
            Choose a form template to assign to this client. They'll see it in their portal.
          </DialogContentText>
          <TextField
            select
            fullWidth
            label="Form template"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            {(consultantData?.formTemplates || []).map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {getLocalizedValue(template.name, isFrench)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={() => setOpenAssign(false)} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={!selectedTemplateId}>Assign</Button>
        </DialogActions>
      </Dialog>

      {/* VIEW ANSWERS DIALOG */}
      <Dialog open={!!viewedForm} onClose={() => setViewedForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold text-slate-900">
          {viewedForm && getLocalizedValue(viewedForm.name, isFrench)}
        </DialogTitle>
        <DialogContent className="space-y-6 pt-2">
          {viewedForm && (() => {
            const answersMap = {};
            viewedForm.fields.forEach((f) => { answersMap[f.fieldKey] = f.answer; });
            const groups = groupFieldsBySteps(viewedForm.fields, viewedForm.steps);
            return groups.map((group) => {
              const visible = group.fields.filter((f) => isFieldVisible(f, answersMap));
              if (visible.length === 0) return null;
              return (
                <div key={group.stepKey} className="space-y-4">
                  {group.title && (
                    <h4 className="text-xs font-black uppercase text-slate-400 tracking-[0.15em] pt-2 border-t border-slate-100 first:border-0 first:pt-0">
                      {getLocalizedValue(group.title, isFrench)}
                    </h4>
                  )}
                  {visible.map((field) => (
                    <FormFieldRenderer
                      key={field.fieldKey}
                      field={field}
                      value={field.answer}
                      readOnly
                      isFrench={isFrench}
                    />
                  ))}
                </div>
              );
            });
          })()}
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={() => setViewedForm(null)} variant="outlined" color="inherit">Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TableForm;
